import React, { Component } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Modal from '../../components/Modal';
import { Icon } from '../../components/Icon';

/**
 * StoryBibleModal
 *
 * Displays the story bible from project.metadata.storyBible.
 * The bible is primarily a rich markdown document rendered for reading.
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - project: object
 *  - onRefreshProject: () => Promise<void>
 *  - onTriggerAgentStep: (stepName, extraData) => void
 */
export default class StoryBibleModal extends Component {
  state = {
    regenerating: false,
    error: null,
    userComment: '',
  };

  componentDidUpdate(prevProps) {
    if (!this.props.isOpen && prevProps.isOpen) {
      this.setState({
        regenerating: false,
        error: null,
      });
    }
  }

  getBible() {
    return this.props.project?.metadata?.storyBible || null;
  }

  handleRegenerate = () => {
    const { project } = this.props;
    if (!project?.id) return;

    const extraData = { forceRerun: true };
    if (this.state.userComment.trim()) {
      extraData.userComment = this.state.userComment.trim();
    }

    this.setState({ userComment: '' });
    this.props.onClose();
    this.props.onTriggerAgentStep?.('StoryBibleStep', extraData);
  };

  renderEmpty() {
    return (
      <div className="sb-empty">
        <Icon.StoryBible style={{ width: 48, height: 48, opacity: 0.3 }} />
        <p>No story bible generated yet.</p>
        <p className="sb-empty-hint">
          Run the agent to create a story bible from your chapters.
        </p>
        <button
          type="button"
          className="btn primary"
          onClick={this.handleRegenerate}
          disabled={this.state.regenerating}
        >
          {this.state.regenerating ? 'Generating…' : 'Generate Bible'}
        </button>
      </div>
    );
  }

  renderBible() {
    const bible = this.getBible();
    const markdown = bible.markdown || '';

    return (
      <div className="sb-content">
        {/* Stats bar */}
        <div className="sb-stats">
          {bible.characters?.length > 0 && (
            <span className="sb-stat">{bible.characters.length} characters</span>
          )}
          {bible.narrativeStructure?.length > 0 && (
            <span className="sb-stat">{bible.narrativeStructure.length} phases</span>
          )}
          {bible.genre && (
            <span className="sb-stat">{bible.genre}</span>
          )}
          {bible.storyBibleGeneratedAt || this.props.project?.metadata?.storyBibleGeneratedAt ? (
            <span className="sb-stat sb-stat-date">
              Generated {new Date(bible.storyBibleGeneratedAt || this.props.project.metadata.storyBibleGeneratedAt).toLocaleDateString()}
            </span>
          ) : null}
        </div>

        {/* Markdown body */}
        <div className="sb-markdown-body">
          <ReactMarkdown className="sb-markdown" remarkPlugins={[remarkGfm]}>
            {markdown}
          </ReactMarkdown>
        </div>
      </div>
    );
  }

  render() {
    const { isOpen, onClose } = this.props;
    const { regenerating, error } = this.state;
    const bible = this.getBible();

    if (!isOpen) return null;

    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="sb-modal-content">
          <div className="sb-modal-header">
            <h2 className="sb-modal-title">Story Bible</h2>
            <div className="sb-modal-actions">
              {bible && (
                <button
                  type="button"
                  className="btn subtle"
                  onClick={this.handleRegenerate}
                  disabled={regenerating}
                  title="Regenerate bible"
                >
                  <Icon.Restart /> Regenerate
                </button>
              )}
            </div>
          </div>

          {error && <div className="sb-error">{error}</div>}

          {regenerating ? (
            <div className="sb-loading">
              <div className="agent-spinner" aria-hidden="true" />
              <span>Generating story bible…</span>
            </div>
          ) : bible ? (
            this.renderBible()
          ) : (
            this.renderEmpty()
          )}

          {!regenerating && bible && (
            <div className="sb-comment-section">
              <div className="sb-comment-label">Revision Notes</div>
              <div className="sb-comment-row">
                <textarea
                  className="sb-comment-input"
                  value={this.state.userComment}
                  onChange={(e) => this.setState({ userComment: e.target.value })}
                  placeholder="Add context or focus areas for the next generation..."
                  rows={2}
                />
                <button
                  type="button"
                  className="btn primary sb-submit-btn"
                  onClick={this.handleRegenerate}
                  disabled={regenerating || !this.state.userComment.trim()}
                >
                  <Icon.Restart /> Submit
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    );
  }
}
